
import { useEffect, useState } from 'react';
import { LikeButton } from '../../../components/engagement/LikeButton';

interface Artist {
  kpis: {
    followers: number;
    topCity: string;
  };
  hasNewRelease: boolean;
}

interface MembershipTier {
  id: string;
  name: string;
  price: number;
}

interface SupportCardProps {
  artist: Artist;
  onTip: () => void;
  membershipTiers: MembershipTier[];
  onJoinMembership: (tierId: string) => void;
  artistId: number;
}

export default function SupportCard({
  artist,
  onTip,
  membershipTiers,
  onJoinMembership,
  artistId
}: SupportCardProps) {
  const [likesCount, setLikesCount] = useState(artist.kpis.followers);

  useEffect(() => {
    setLikesCount(artist.kpis.followers);
  }, [artist.kpis.followers]);

  const formatFollowers = (count: number) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return count.toString();
  };

  const primaryTier = membershipTiers[0];

  return (
    <div className="w-full lg:w-80 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-6 border border-emerald-200">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Support this artist directly
        </h3>
        <p className="text-sm text-gray-600">
          Own the music, unlock perks, fund new releases
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        {artist.hasNewRelease && (
          <div className="text-center">
            <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 mb-1">
              <i className="ri-fire-line mr-1"></i>
              New release
            </div>
          </div>
        )}
        <div className="text-center">
          <p className="text-sm text-gray-500">Top city</p>
          <p className="font-semibold text-gray-900">{artist.kpis.topCity}</p>
        </div>
        <div className="text-center">
          <p className="text-sm text-gray-500">Likes</p>
          <p className="font-semibold text-gray-900">{formatFollowers(likesCount)}</p>
        </div>
      </div>

      {/* Primary Actions */}
      <div className="space-y-3 mb-6">
        {primaryTier ? (
          <button
            onClick={() => onJoinMembership(primaryTier.id)}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors whitespace-nowrap"
          >
            Join {primaryTier.name} - KES {primaryTier.price}
          </button>
        ) : (
          <button
            onClick={onTip}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors whitespace-nowrap"
          >
            <i className="ri-hand-heart-line mr-2"></i>
            Tip Artist
          </button>
        )}

        <LikeButton
          subjectType="artist"
          subjectId={artistId}
          initialCount={likesCount}
          variant="pill"
          className="w-full justify-center bg-white hover:bg-gray-50 text-gray-900"
          onCountChange={setLikesCount}
          label="Like"
        />
      </div>

      {/* Trust Indicators */}
      <div className="space-y-2 text-xs text-gray-600">
        <div className="flex items-center">
          <i className="ri-shield-check-line text-emerald-600 mr-2"></i>
          MPESA & Cards • Instant Access
        </div>
        <div className="flex items-center">
          <i className="ri-download-line text-emerald-600 mr-2"></i>
          Instant downloads with purchase
        </div>
        <div className="flex items-center">
          <i className="ri-user-heart-line text-emerald-600 mr-2"></i>
          Tips go straight to the artist
        </div>
        {membershipTiers.length > 0 && (
          <div className="flex items-center">
            <i className="ri-vip-crown-line text-emerald-600 mr-2"></i>
            Member-only drops & early access
          </div>
        )}
      </div>
    </div>
  );
}
